// Placing Fleet Live's panels into their zones (#6779, prototype `place` / `relayout`,
// `:3530-3539`, `:3605-3632`).
//
// THE PANELS ARE MOVED, NEVER RE-MOUNTED. Each panel is rendered once and its wrap element is
// appended into its zone's element in stacking order (the prototype's `place()`), so a move
// keeps the Core's canvas, its squad focus, the Merge Gate's subject and its read. A per-zone
// `<For>` would dispose the moved panel and mount a fresh one. Only these wraps are moved,
// and only into this section's own zone elements, which the same `<Show>` mounted, so they
// leave the DOM together.
//
// A RELAYOUT ANIMATES 560 ms (each moved wrap slides from its old box, FLIP), and a moved
// panel replays a 900 ms flicker. Under reduced motion, or where `Element.animate` is missing
// (the test DOM), both take effect at once.

import { type LiveLayout, type LiveZone, panelsIn } from './fleet-live-layout';
import type { FleetLivePanel } from './fleet-live-vis';

export const RELAYOUT_MS = 560;
export const FLICKER_MS = 900;
const EASE = 'cubic-bezier(0.16,1,0.3,1)';

export type ZoneEls = Partial<Record<LiveZone, HTMLElement>>;
export type WrapEls = Partial<Record<FleetLivePanel, HTMLElement>>;

/**
 * Append every wrap into its zone, in order. A zone already holding its panels in order is
 * left alone, so a focused control inside an unmoved panel keeps its focus.
 */
export function placePanels(layout: LiveLayout, zones: ZoneEls, wraps: WrapEls): void {
  const ours = new Set(Object.values(wraps));
  for (const zone of ['top', 'middle', 'side', 'bottom'] as const) {
    const el = zones[zone];
    if (el === undefined) continue;
    const want = panelsIn(layout, zone)
      .map((k) => wraps[k])
      .filter((w): w is HTMLElement => w !== undefined);
    const have = Array.from(el.children).filter((c) => ours.has(c as HTMLElement));
    if (have.length === want.length && have.every((c, i) => c === want[i])) continue;
    for (const w of want) el.appendChild(w);
  }
}

const canAnimate = (el: HTMLElement): boolean => typeof el.animate === 'function';

/** Place the wraps and slide each shown one from where it was. */
export function relayout(
  layout: LiveLayout,
  zones: ZoneEls,
  wraps: WrapEls,
  motion: boolean,
): void {
  const before = new Map<HTMLElement, DOMRect>();
  for (const w of Object.values(wraps)) {
    if (w !== undefined && !w.hidden) before.set(w, w.getBoundingClientRect());
  }
  placePanels(layout, zones, wraps);
  if (!motion) return;
  for (const [w, o] of before) {
    if (!canAnimate(w) || w.hidden) continue;
    const n = w.getBoundingClientRect();
    if (n.width === 0 || n.height === 0) continue;
    const dx = o.left - n.left;
    const dy = o.top - n.top;
    const sx = o.width / n.width;
    const sy = o.height / n.height;
    if (Math.abs(dx) + Math.abs(dy) < 1 && Math.abs(sx - 1) < 0.01) continue;
    w.animate(
      [
        { transformOrigin: '0 0', transform: `translate(${dx}px,${dy}px) scale(${sx},${sy})` },
        { transformOrigin: '0 0', transform: 'none' },
      ],
      { duration: RELAYOUT_MS, easing: EASE },
    );
  }
}

/** Replay a panel's flicker (the prototype's `fl-flick` on the content; brief.md item 77). */
export function flicker(wrap: HTMLElement | undefined, motion: boolean): void {
  if (wrap === undefined || !motion || !canAnimate(wrap)) return;
  wrap.animate(
    [
      { opacity: 0 },
      { opacity: 0.7, offset: 0.09 },
      { opacity: 0.15, offset: 0.13 },
      { opacity: 0.9, offset: 0.22 },
      { opacity: 0.4, offset: 0.28 },
      { opacity: 1, offset: 0.42 },
      { opacity: 1 },
    ],
    { duration: FLICKER_MS, easing: 'linear' },
  );
}
